import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, projectApi, type Project } from '../api';
import { useCan } from './useMe';
import { ImportModal } from './ImportModal';
import { useProjectActions } from './ProjectActions';
import { useContextMenu } from '../ui/ContextMenu';
import { ProjectSummary } from './ProjectSummary';
import { PageScroll } from '../ui/PageScroll';
import { isNewTabClick, openInNewTab } from '../ui/links';
import { PlusTile } from '../ui/PlusTile';

/**
 * The start page: the office's projects.
 *
 * A project is opened by clicking anywhere on its tile, or with Enter when the
 * tile has keyboard focus. The search filters by number and name as you type -
 * worth having as soon as there are more projects than fit on one screen.
 */
export function ProjectsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [opened, dialog] = useDisclosure();
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const canCreate = useCan('projects.write');
  const [importOpen, importDialog] = useDisclosure();
  const actions = useProjectActions();
  const context = useContextMenu<Project>((project, close) => actions.items(project, close));
  // The ⋯ menu open on a tile; controlled so an entry in a submenu closes it too.
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });

  const create = useMutation({
    mutationFn: () => projectApi.create({ number, name }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      setNumber('');
      setName('');
      dialog.close();
      // Straight into the new project: nobody creates one to look at a list.
      navigate(`/projekte/${created.id}`);
    },
  });

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = projects.data ?? [];
    if (!needle) return all;
    return all.filter(
      (project) =>
        project.number.toLowerCase().includes(needle) ||
        project.name.toLowerCase().includes(needle),
    );
  }, [projects.data, search]);

  const forbidden = create.error instanceof ApiError && create.error.status === 403;
  const open = (id: string): void => {
    void navigate(`/projekte/${id}`);
  };

  return (
    <PageScroll>
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Title order={3}>Projekte</Title>
        <Group gap="xs" wrap="nowrap">
          {(projects.data ?? []).length > 6 && (
            <TextInput
              placeholder="Nummer oder Bezeichnung"
              aria-label="Projekte durchsuchen"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              size="sm"
            />
          )}
          {canCreate && <Button variant="default" onClick={actions.startFromTemplate}>Aus Vorlage</Button>}
          {canCreate && <Button variant="default" onClick={importDialog.open}>Importieren</Button>}
          {canCreate && <Button onClick={dialog.open}>Projekt anlegen</Button>}
        </Group>
      </Group>

      {projects.isSuccess && (projects.data ?? []).length === 0 && (
        <Card withBorder p="xl">
          <Stack align="center" gap="xs">
            {canCreate ? (
              <>
                <Text>Noch keine Projekte.</Text>
                <Button variant="light" onClick={dialog.open}>Erstes Projekt anlegen</Button>
              </>
            ) : (
              <Text ta="center">
                Sie sind noch keinem Projekt zugeordnet. Die Projektleitung kann Sie im
                Reiter „Team“ eines Projekts hinzufügen.
              </Text>
            )}
          </Stack>
        </Card>
      )}

      {search && shown.length === 0 && (
        <Text c="dimmed" size="sm">Kein Projekt passt zu „{search}".</Text>
      )}

      <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: 4 }} spacing="sm">
        {canCreate && !search.trim() && <PlusTile label="Neues Projekt" hint="Nummer und Name – oder aus Vorlage, per Import" onClick={dialog.open} />}
        {shown.map((project) => (
          <Card
            key={project.id}
            withBorder
            padding="md"
            role="link"
            tabIndex={0}
            aria-label={`Projekt ${project.number} ${project.name} öffnen`}
            onClick={(event) => (isNewTabClick(event) ? openInNewTab(`/projekte/${project.id}`) : open(project.id))}
            onAuxClick={(event) => { if (event.button === 1) openInNewTab(`/projekte/${project.id}`); }}
            onContextMenu={(event) => context.open(event, project)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                open(project.id);
              }
            }}
            className="pst-tile"
          >
            <Group justify="space-between" wrap="nowrap" align="flex-start" gap={4}>
              <Text size="xs" c="dimmed" ff="monospace">{project.number}</Text>
              <Menu position="bottom-end" withinPortal width={240} opened={menuFor === project.id} onChange={(o) => setMenuFor(o ? project.id : null)}>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label={`Aktionen für ${project.number}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    mt={-4}
                    mr={-6}
                  >
                    {'⋯'}
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>{actions.items(project, () => setMenuFor(null))}</Menu.Dropdown>
              </Menu>
            </Group>
            <Text fw={600} lineClamp={2} mt={2}>{project.name}</Text>
            <ProjectSummary projectId={project.id} />
          </Card>
        ))}
      </SimpleGrid>

      <ImportModal opened={importOpen} onClose={importDialog.close} />
      {actions.dialogs}
      {context.menu}
      <Modal opened={opened} onClose={dialog.close} title="Projekt anlegen">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <Stack>
            {create.isError && (
              <Alert color="red">
                {forbidden
                  ? 'Ihre Rolle darf keine Projekte anlegen.'
                  : 'Anlegen fehlgeschlagen. Ist die Projektnummer schon vergeben?'}
              </Alert>
            )}
            <TextInput
              label="Projektnummer"
              description="bürointerne Nummer, z. B. 2026-014"
              required
              data-autofocus
              value={number}
              onChange={(event) => setNumber(event.currentTarget.value)}
            />
            <TextInput
              label="Bezeichnung"
              required
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
            />
            <Button type="submit" loading={create.isPending}>Anlegen</Button>
          </Stack>
        </form>
      </Modal>
    </Stack>
    </PageScroll>
  );
}
